import { TourDefinition } from '../types/tour.types';
import { isHidden } from '../config/hiddenFeatures';

/**
 * Partner HQ Overview Tour
 * Introduces new partners to the main navigation and workspace
 */
export const partnerHQOverviewTour: TourDefinition = {
  id: 'partner-hq-overview',
  name: 'Partner HQ Overview',
  description: 'Learn how to navigate your Partner HQ workspace',
  category: 'getting-started',
  estimatedMinutes: 3,
  targetPage: '/propcloud-partner',
  steps: [
    {
      target: '[data-tour="partner-hq-header"]',
      title: 'Welcome to Partner HQ',
      content: 'This is your command center for managing your real estate business. Let\'s take a quick tour of the key areas.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-help-tours"]',
      title: 'Help & Tours',
      content: 'Access the setup wizard, interactive tours, and integration settings here anytime you need guidance.',
      placement: 'bottom',
    },
    // Skipped while the Manage Agents menu is hidden (a missing target would stall the tour).
    ...(isHidden('partnerHqManageAgents') ? [] : [{
      target: '[data-tour="nav-manage-agents"]',
      title: 'Manage Your Team',
      content: 'Add and manage your sales agents here. You can also view their commissions and performance metrics.',
      placement: 'bottom' as const,
    }]),
    {
      target: '[data-tour="nav-round-robin"]',
      title: 'Lead Distribution',
      content: 'Configure how leads are automatically distributed to your team using our smart Round Robin system.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-marketing-hub"]',
      title: 'Marketing Hub',
      content: 'Create email, SMS, and WhatsApp campaigns to nurture leads and stay connected with clients.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-properties-website"]',
      title: 'Property Management',
      content: 'List your own properties for sale or view the HQ development inventory available to you.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="partner-hq-workspace"]',
      title: 'Your Workspace',
      content: 'When you click menu items, windows open here. You can have multiple windows open, drag them around, and resize them.',
      placement: 'center',
    },
    {
      target: '[data-tour="back-to-dashboard"]',
      title: 'Return to Dashboard',
      content: 'Click here anytime to go back to your main dashboard. You\'re all set to explore!',
      placement: 'bottom',
    },
  ],
};

/**
 * Add Your First Agent Tour
 * Guides partners through adding their first team member
 */
export const addFirstAgentTour: TourDefinition = {
  id: 'add-first-agent',
  name: 'Add Your First Agent',
  description: 'Learn how to invite team members to your organization',
  category: 'getting-started',
  estimatedMinutes: 2,
  targetPage: '/propcloud-partner',
  steps: [
    {
      target: '[data-tour="nav-manage-agents"]',
      title: 'Open Manage Agents',
      content: 'Click here to access the agent management menu.',
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="add-agent-menu-item"]',
      title: 'Add New Agent',
      content: 'Select "Add New Agent" to open the agent creation form.',
      placement: 'right',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="agent-basic-info"]',
      title: 'Basic Information',
      content: 'Enter the agent\'s name, email, and mobile number. They\'ll receive an email invitation to join.',
      placement: 'right',
    },
    {
      target: '[data-tour="agent-commission"]',
      title: 'Commission Structure',
      content: 'Set the commission percentage this agent will earn from your 70% partner commission.',
      placement: 'right',
    },
    {
      target: '[data-tour="agent-round-robin"]',
      title: 'Lead Distribution',
      content: 'Choose whether this agent should receive leads via the Round Robin system.',
      placement: 'right',
    },
    {
      target: '[data-tour="agent-save"]',
      title: 'Save and Invite',
      content: 'Click Save to create the agent. They\'ll receive an email invitation to complete their profile and start working.',
      placement: 'top',
    },
  ],
};

/**
 * Create Email Campaign Tour
 * Walks through creating an email marketing campaign
 */
export const createEmailCampaignTour: TourDefinition = {
  id: 'create-email-campaign',
  name: 'Create an Email Campaign',
  description: 'Learn how to create and send email marketing campaigns',
  category: 'features',
  estimatedMinutes: 5,
  targetPage: '/propcloud-partner',
  steps: [
    {
      target: '[data-tour="nav-marketing-hub"]',
      title: 'Open Marketing Hub',
      content: 'Click to access the campaign creation tools.',
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="menu-item-email-campaigns"]',
      title: 'Email Campaigns',
      content: 'Select "Email Campaigns" to view and create email campaigns.',
      placement: 'right',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="create-campaign-button"]',
      title: 'Create New Campaign',
      content: 'Click here to start creating a new email campaign.',
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="campaign-name"]',
      title: 'Campaign Name',
      content: 'Give your campaign a descriptive name so you can easily find it later.',
      placement: 'right',
    },
    {
      target: '[data-tour="campaign-subject"]',
      title: 'Email Subject Line',
      content: 'Write a compelling subject line. This is the first thing recipients see in their inbox!',
      placement: 'right',
    },
    {
      target: '[data-tour="campaign-template"]',
      title: 'Choose Template',
      content: 'Select from pre-designed templates or start from scratch with a blank canvas.',
      placement: 'left',
    },
    {
      target: '[data-tour="campaign-editor"]',
      title: 'Email Editor',
      content: 'Customize your email content here. Use variables like {{firstName}} to personalize each message.',
      placement: 'top',
    },
    {
      target: '[data-tour="campaign-audience"]',
      title: 'Select Recipients',
      content: 'Choose which contacts should receive this email. You can filter by tags, source, or status.',
      placement: 'left',
    },
    {
      target: '[data-tour="campaign-preview"]',
      title: 'Preview & Test',
      content: 'Always preview your email and send a test to yourself before the real send.',
      placement: 'left',
    },
    {
      target: '[data-tour="campaign-send"]',
      title: 'Send Campaign',
      content: 'When ready, click Send to deliver your campaign immediately, or schedule it for later.',
      placement: 'top',
    },
  ],
};

/**
 * CRM Basics Tour
 * Introduces the CRM contact management system
 */
export const crmBasicsTour: TourDefinition = {
  id: 'crm-basics',
  name: 'CRM Basics',
  description: 'Learn how to view and manage your contacts',
  category: 'features',
  estimatedMinutes: 3,
  targetPage: '/crm',
  steps: [
    {
      target: '[data-tour="crm-nav-link"]',
      title: 'Access CRM',
      content: 'Navigate to the CRM from your dashboard to manage all your contacts.',
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="crm-contact-list"]',
      title: 'Contact List',
      content: 'View all your contacts here. Click any contact to see their full profile and history.',
      placement: 'right',
    },
    {
      target: '[data-tour="crm-filters"]',
      title: 'Filter Contacts',
      content: 'Use these filters to find contacts by source, status, assigned agent, tags, and more.',
      placement: 'left',
    },
    {
      target: '[data-tour="crm-search"]',
      title: 'Quick Search',
      content: 'Search by name, email, or phone number to quickly find specific contacts.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="crm-contact-detail"]',
      title: 'Contact Profile',
      content: 'View the full contact profile including communication history, lead score, and notes.',
      placement: 'left',
    },
    {
      target: '[data-tour="crm-actions"]',
      title: 'Quick Actions',
      content: 'Send emails, SMS messages, or schedule follow-ups directly from any contact card.',
      placement: 'top',
    },
    {
      target: '[data-tour="crm-pipeline"]',
      title: 'Pipeline View',
      content: 'Switch to the pipeline view to see contacts organized by their sales stage.',
      placement: 'bottom',
    },
  ],
};

/**
 * Lead Distribution Tour
 * Explains the Round Robin lead distribution system
 */
export const leadDistributionTour: TourDefinition = {
  id: 'lead-distribution',
  name: 'Lead Distribution',
  description: 'Understand how leads are automatically distributed to your team',
  category: 'features',
  estimatedMinutes: 2,
  targetPage: '/propcloud-partner',
  steps: [
    {
      target: '[data-tour="nav-round-robin"]',
      title: 'Round Robin Settings',
      content: 'Click to access lead distribution configuration.',
      placement: 'bottom',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="round-robin-overview"]',
      title: 'Distribution Overview',
      content: 'This shows how leads have been distributed across your team over time.',
      placement: 'right',
    },
    {
      target: '[data-tour="round-robin-enable"]',
      title: 'Enable Round Robin',
      content: 'Toggle this on to automatically distribute incoming leads to your active agents.',
      placement: 'right',
    },
    {
      target: '[data-tour="round-robin-agents"]',
      title: 'Active Agents',
      content: 'Only agents shown here will receive leads. Inactive agents are excluded automatically.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="round-robin-algorithm"]',
      title: 'Smart Distribution',
      content: 'First 50 leads: equal distribution. After that, top performers get more leads based on their OTP conversion rate.',
      placement: 'left',
    },
    {
      target: '[data-tour="round-robin-history"]',
      title: 'Assignment History',
      content: 'View the complete history of lead assignments for full transparency and auditing.',
      placement: 'top',
    },
  ],
};

/**
 * CRM v2 — interactive walkthrough.
 * Auto-starts on a consultant's first visit to /crm-v2 (see AutoStartTour
 * component) and replayable from the v2 Settings page. Targets stable
 * `data-tour="..."` anchors added on the relevant v2 components.
 */
export const crmV2InteractiveTour: TourDefinition = {
  id: 'crm-v2-interactive-tour',
  name: 'CRM v2 — Take a quick tour',
  description: 'A 3-minute walkthrough of the simplified CRM.',
  category: 'getting-started',
  estimatedMinutes: 3,
  targetPage: '/crm-v2',
  steps: [
    {
      target: '[data-tour="v2-sidebar"]',
      title: 'Welcome to the new CRM ✨',
      content:
        'Six menu items replace the previous eighteen — Inbox, Pipeline, Contacts, Calendar, Resources, Settings. Everything else folds into them.',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="v2-inbox-list"]',
      title: 'Inbox — one unified list',
      content:
        'Every WhatsApp, email, SMS, web form and ad lead lands here in one place. No more checking five separate views.',
      placement: 'top',
    },
    {
      target: '[data-tour="v2-today-widget"]',
      title: 'Today at a glance',
      content:
        "Click any card to focus on today's new leads, follow-ups due, viewings, or snoozed contacts waking up.",
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-inbox-toolbar"]',
      title: 'Search, sort and density',
      content:
        'Search by name, phone or email. Sort by received, last activity, name, development or source. Switch between comfortable and compact rows.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-inbox-filters"]',
      title: 'Filter chips',
      content:
        'Click any source (META, WhatsApp, Google Ads), pipeline stage, or smart chip (Unactioned / Needs reply / Snoozed) to narrow the list. Combine as many as you like.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-saved-views"]',
      title: 'Save your own views',
      content:
        'Built a useful filter combination? Save it as a one-click chip — "Hot WhatsApp", "Needs follow-up", whatever you use most. Each consultant has their own private set.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-inbox-row"]',
      title: 'Click any lead',
      content:
        'Clicking a row opens the detail drawer on the right. Try clicking one now — the tour will continue inside it.',
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="v2-drawer-tabs"]',
      title: 'Four tabs in the drawer',
      content:
        'Overview for contact info, tags, DNC. Conversation for unified history + email reply (via your Gmail). Activity for tasks, notes and viewings. Files for documents + credit score.',
      placement: 'left',
    },
    {
      target: '[data-tour="v2-snooze-button"]',
      title: 'Snooze a lead',
      content:
        'Hide it for an hour, a day, a week or a custom date. It comes back to your Inbox automatically when the snooze expires.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-pipeline-link"]',
      title: 'Switch to Kanban',
      content:
        'Click "Kanban →" any time to see the same leads as a drag-and-drop board organised by pipeline stage. Same data, different lens.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="v2-bulk-checkbox"]',
      title: 'Bulk actions',
      content:
        'Tick the checkbox on any row (or several) to bring up the bulk-action bar — tag, mark DNC, change stage, snooze, all at once.',
      placement: 'right',
    },
    {
      target: '[data-tour="v2-sidebar"]',
      title: "You're set ⌘K",
      content:
        'Press ⌘K (or Ctrl+K) anywhere in the CRM to find any contact, development, task or viewing. Press ? for the full keyboard shortcut list. You can replay this tour any time from Settings.',
      placement: 'right',
    },
  ],
};

/**
 * All available tours
 */
export const ALL_TOURS: TourDefinition[] = [
  partnerHQOverviewTour,
  // Walks through the Manage Agents menu, which is hidden via src/config/hiddenFeatures.ts.
  ...(isHidden('partnerHqManageAgents') ? [] : [addFirstAgentTour]),
  createEmailCampaignTour,
  crmBasicsTour,
  leadDistributionTour,
  crmV2InteractiveTour,
];

/**
 * Get tour by ID
 */
export function getTourById(tourId: string): TourDefinition | undefined {
  return ALL_TOURS.find(tour => tour.id === tourId);
}

/**
 * Get tours by category
 */
export function getToursByCategory(category: TourDefinition['category']): TourDefinition[] {
  return ALL_TOURS.filter(tour => tour.category === category);
}
